import { LocalParticipant, Participant, Track } from 'livekit-client';
import {
    isGuestParticipant,
    isMainModeratorParticipant,
    isModeratorParticipant,
} from 'useCase/moderation/status/moderators';
import { isParticipantSpeaker } from 'useCase/webinar/webinarStatus';

export type CheckIsHandRaised = (participant: Participant) => boolean | undefined;

const isParticipantScreenSharer = (participant: Participant): boolean => {
    const screenSharePublication = participant.getTrackPublication(Track.Source.ScreenShare);
    return Boolean(screenSharePublication) || participant.isScreenShareEnabled;
};

/**
 * Сортировка для списка участников в UI:
 * 1) Локальный пользователь
 * 2) Шаринг экрана
 * 3) Основной модератор
 * 4) модераторы
 * 5) спикеры (только в режиме вебинара)
 * 6) авторизованные пользователи
 * 7) гости
 *
 * Внутри групп порядок: dominant speaker -> локальный -> недавно говорившие -> с видео -> по joinedAt.
 */
export function participantListOrderSort(
    participants: Participant[],
    checkIsHandRaised: CheckIsHandRaised,
): Participant[] {
    const local = participants.find((p) => p instanceof LocalParticipant);
    const rest = participants.filter((p) => !(p instanceof LocalParticipant));
    const defaultSortedRest = defaultSortParticipants(rest, checkIsHandRaised);

    const { screenSharers, notScreenSharers } = defaultSortedRest.reduce<{
        screenSharers: Participant[];
        notScreenSharers: Participant[];
    }>(
        (acc, p) => {
            isParticipantScreenSharer(p) ? acc.screenSharers.push(p) : acc.notScreenSharers.push(p);
            return acc;
        },
        { screenSharers: [], notScreenSharers: [] },
    );

    const { mainMod, otherModerators, speakers, authorized, guests } = notScreenSharers.reduce<{
        mainMod: Participant | null;
        otherModerators: Participant[];
        speakers: Participant[];
        authorized: Participant[];
        guests: Participant[];
    }>(
        (acc, p) => {
            if (isMainModeratorParticipant(p) && !acc.mainMod) {
                acc.mainMod = p;
            } else if (isModeratorParticipant(p)) {
                acc.otherModerators.push(p);
            } else if (isParticipantSpeaker(p)) {
                acc.speakers.push(p);
            } else if (isGuestParticipant(p)) {
                acc.guests.push(p);
            } else {
                acc.authorized.push(p);
            }
            return acc;
        },
        { mainMod: null, otherModerators: [], speakers: [], authorized: [], guests: [] },
    );

    const otherModSorted = defaultSortParticipants(otherModerators, checkIsHandRaised);
    const speakersSorted = defaultSortParticipants(speakers, checkIsHandRaised);
    const authorizedSorted = defaultSortParticipants(authorized, checkIsHandRaised);
    const guestsSorted = defaultSortParticipants(guests, checkIsHandRaised);

    return [
        ...(local ? [local] : []),
        ...screenSharers,
        ...(mainMod ? [mainMod] : []),
        ...otherModSorted,
        ...speakersSorted,
        ...authorizedSorted,
        ...guestsSorted,
    ];
}

/**
 * Default order: dominant speaker → local → recently active → video on → joinedAt.
 * Hand-raised participants are ordered before others when not speaking.
 */
function defaultSortParticipants(participants: Participant[], checkIsHandRaised: CheckIsHandRaised): Participant[] {
    const sorted = [...participants];
    sorted.sort((a, b) => {
        if (a.isSpeaking && b.isSpeaking) {
            return b.audioLevel - a.audioLevel;
        }
        if (a.isSpeaking !== b.isSpeaking) {
            return a.isSpeaking ? -1 : 1;
        }

        const handRaisedA = checkIsHandRaised(a);
        const handRaisedB = checkIsHandRaised(b);
        if (handRaisedA && !handRaisedB) return -1;
        if (handRaisedB && !handRaisedA) return 1;

        const aLast = a.lastSpokeAt?.getTime() ?? 0;
        const bLast = b.lastSpokeAt?.getTime() ?? 0;
        if (aLast !== bLast) return bLast - aLast;

        const aVideo = a.videoTrackPublications.size > 0;
        const bVideo = b.videoTrackPublications.size > 0;
        if (aVideo !== bVideo) return aVideo ? -1 : 1;

        return (a.joinedAt?.getTime() ?? 0) - (b.joinedAt?.getTime() ?? 0);
    });
    return sorted;
}
